import React from "react";
import { graphql, useFragment } from "react-relay";

import { useFeatureIsOn } from "@growthbook/growthbook-react";

import { AGENDA_BUILDER_FEATURE_FLAG } from "~/agendaBuilder";
import AppContext from "~/common/AppContext";

import type { featureFlag_useCopilotPaywallsOn_viewer$key as CopilotAddonViewerKey } from "~/__generated__/featureFlag_useCopilotPaywallsOn_viewer.graphql";

export const MEETING_RECORDER_FEATURE_FLAG = "meeting-recorder-and-transcriber";
export const CUSTOM_VOCABULARY_FEATURE_FLAG = "custom-vocabulary";
export const WEB_BOTLESS_RECORDING_FEATURE_FLAG = "microphone-recording";
export const DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG = "desktop-recording";

export function useMeetingRecorderFeatureFlagOn(): boolean {
	return useFeatureIsOn(MEETING_RECORDER_FEATURE_FLAG);
}

export function useMeetingRecorderEnabled(): boolean {
	const appContext = React.useContext(AppContext);
	return !!appContext.user?.hasAccessToCopilot;
}

export function useMeetingRecorderBillingEnabled(): boolean {
	return useFeatureIsOn("meeting_recorder_billing");
}

export function useSchedulingMeetingAssistantFlagOn(): boolean {
	return useFeatureIsOn("scheduling-meeting-assistant");
}

export function useIsOneOfAIsFeatureFlagEnabled() {
	const isSuggestedTopicsEnabled = useFeatureIsOn("ai-suggested-topics");
	const isGenerateAgendaEnabled = useFeatureIsOn("ai-generate-agenda");
	const isAgendaBuilderEnabled = useFeatureIsOn(AGENDA_BUILDER_FEATURE_FLAG);
	const isMeetingRecorderEnabled = useFeatureIsOn(MEETING_RECORDER_FEATURE_FLAG);
	return isSuggestedTopicsEnabled || isGenerateAgendaEnabled || isAgendaBuilderEnabled || isMeetingRecorderEnabled;
}

export function useCopilotPaywallsEnabled(viewerKey: CopilotAddonViewerKey): boolean {
	const isCopilotBillingFeatureFlagOn = useFeatureIsOn("meeting_recorder_billing");

	const {
		account: {
			billingAccount: { hasCopilotAddon },
		},
	} = useFragment(
		graphql`
			fragment featureFlag_useCopilotPaywallsOn_viewer on Viewer {
				account {
					billingAccount {
						hasCopilotAddon: hasAccessToFeature(feature: meeting_recorder)
					}
				}
			}
		`,
		viewerKey,
	);

	return isCopilotBillingFeatureFlagOn && !hasCopilotAddon;
}

export function useCustomVocabularyFeatureFlagOn(): boolean {
	return useFeatureIsOn(CUSTOM_VOCABULARY_FEATURE_FLAG);
}
